import React from 'react';
import {
  LineChart as RechartsLineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { cn } from '../../lib/utils';

interface LineChartProps {
  /** Chart data */
  data: any[];
  /** Chart title */
  title?: string;
  /** Chart height in pixels */
  height?: number;
  /** Custom className */
  className?: string;
  /** X-axis data key */
  xAxisKey?: string;
  /** Line data keys */
  lines?: Array<{
    dataKey: string;
    stroke: string;
    name: string;
  }>;
}

/**
 * LineChart wrapper component for time-series data
 *
 * @example
 * ```tsx
 * <LineChart
 *   data={dailyStats}
 *   title="Traffic Over Time"
 *   xAxisKey="date"
 *   lines={[
 *     { dataKey: 'human', stroke: '#10b981', name: 'Human' },
 *     { dataKey: 'bot', stroke: '#ef4444', name: 'Bot' }
 *   ]}
 * />
 * ```
 */
export const LineChart: React.FC<LineChartProps> = ({
  data,
  title,
  height = 300,
  className,
  xAxisKey = 'date',
  lines = [
    { dataKey: 'human', stroke: '#10b981', name: 'Human' },
    { dataKey: 'bot', stroke: '#ef4444', name: 'Bot' },
  ],
}) => {
  // Show empty state if no data
  if (!data || data.length === 0) {
    return (
      <div
        className={cn(
          'flex items-center justify-center bg-gray-50 rounded-lg border border-gray-200',
          className
        )}
        style={{ height }}
      >
        <p className="text-gray-500">No data available</p>
      </div>
    );
  }

  return (
    <div className={cn('w-full', className)}>
      {title && <h3 className="text-lg font-semibold mb-4">{title}</h3>}
      <ResponsiveContainer width="100%" height={height}>
        <RechartsLineChart
          data={data}
          margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey={xAxisKey} />
          <YAxis />
          <Tooltip />
          <Legend />
          {lines.map((line) => (
            <Line
              key={line.dataKey}
              type="monotone"
              dataKey={line.dataKey}
              stroke={line.stroke}
              name={line.name}
              strokeWidth={2}
              dot={false}
            />
          ))}
        </RechartsLineChart>
      </ResponsiveContainer>
    </div>
  );
};

export default LineChart;
